"use client";

import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { StateAutocomplete } from "@/components/inputs/state-autocomplete";
import { ZipInput } from "@/components/inputs/zip-input";
import type { ZipLocation } from "@/hooks/use-zip-geocoding";
import { countryFor, type ManualAddressDraft } from "./use-manual-address-draft";

export function ManualAddressActions({
  submitLabel,
  canSubmit,
  onCancel,
  onSubmit,
}: {
  submitLabel: string;
  canSubmit: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="flex justify-end gap-2">
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        type="button"
        variant="primary"
        size="sm"
        disabled={!canSubmit}
        onClick={onSubmit}
      >
        {submitLabel}
      </Button>
    </div>
  );
}

export function RegionPostalRow({
  idPrefix,
  draft,
  update,
  onZipGeocode,
  zipError,
  includeCanada,
  compact,
}: Omit<DraftFieldProps, "id"> & {
  idPrefix: string;
  onZipGeocode: (result: ZipLocation | null) => void;
  zipError: string | undefined;
  includeCanada: boolean;
}) {
  return (
    <div className="grid grid-cols-2 items-start gap-3">
      <RegionField
        id={`${idPrefix}-state`}
        draft={draft}
        update={update}
        includeCanada={includeCanada}
        compact={compact}
      />
      <PostalCodeField
        id={`${idPrefix}-postal-code`}
        draft={draft}
        update={update}
        onZipGeocode={onZipGeocode}
        zipError={zipError}
        compact={compact}
      />
    </div>
  );
}

interface DraftFieldProps {
  id: string;
  draft: ManualAddressDraft;
  update: (patch: Partial<ManualAddressDraft>) => void;
  compact: boolean;
}

function RegionField({
  id,
  draft,
  update,
  includeCanada,
  compact,
}: DraftFieldProps & { includeCanada: boolean }) {
  return (
    <Field controlId={id} className="gap-1.5">
      <FieldLabel>{includeCanada ? "State or province" : "State"}</FieldLabel>
      <StateAutocomplete
        id={id}
        value={draft.state}
        onChange={(state) => update({ state })}
        placeholder="Select"
        includeCanada={includeCanada}
        compact={compact}
      />
    </Field>
  );
}

function PostalCodeField({
  id,
  draft,
  update,
  onZipGeocode,
  zipError,
  compact,
}: DraftFieldProps & {
  onZipGeocode: (result: ZipLocation | null) => void;
  zipError: string | undefined;
}) {
  const isCanada = countryFor(draft.state) === "CA";
  return (
    <Field controlId={id} className="gap-1.5" data-invalid={Boolean(zipError)}>
      <FieldLabel>{isCanada ? "Postal code" : "ZIP code"}</FieldLabel>
      {isCanada ? (
        <Input
          id={id}
          value={draft.postalCode}
          onChange={(event) => update({ postalCode: event.target.value.toUpperCase() })}
          autoComplete="postal-code"
          placeholder="K1A 0B1"
          aria-invalid={Boolean(zipError)}
          size={compact ? "compact" : "default"}
        />
      ) : (
        <ZipInput
          id={id}
          value={draft.postalCode}
          onChange={(postalCode) => update({ postalCode })}
          onGeocode={onZipGeocode}
          placeholder="12345"
          compact={compact}
          aria-invalid={Boolean(zipError)}
        />
      )}
      <FieldError>{zipError}</FieldError>
    </Field>
  );
}

export function ManualTextField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  maxLength,
  placeholder,
  optional,
  compact,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
  maxLength: number;
  placeholder?: string;
  optional?: boolean;
  compact: boolean;
}) {
  return (
    <Field controlId={id} className="gap-1.5">
      <FieldLabel optional={optional}>{label}</FieldLabel>
      <Input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        maxLength={maxLength}
        placeholder={placeholder}
        size={compact ? "compact" : "default"}
      />
    </Field>
  );
}
